import React, { useState, useEffect } from 'react';
import { usePayment } from '../../contexts/PaymentContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import { CheckCircle, AlertCircle, Calendar, Activity, Phone, Heart, Baby, ArrowRight, Clock } from 'lucide-react';

const PatientDashboard = () => {
  const { subscription, hasActiveSubscription } = usePayment();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    // Check if user needs to change password on first login
    if (user?.requiresPasswordChange) {
      setShowPasswordModal(true);
    }
  }, [user]);

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  const mockAppointments = [
    {
      id: '1',
      date: now.toISOString(),
      time: `${currentHour}:${currentMinute.toString().padStart(2, '0')}`,
      doctor: 'Dr. Amina Yusuf',
      specialty: 'OB/GYN Specialist',
      type: 'Antenatal Check-up',
      mode: 'video',
      status: 'confirmed',
      scheduledTime: new Date(new Date().setHours(currentHour, currentMinute, 0, 0)),
    },
    {
      id: '2',
      date: now.toISOString(),
      time: `${(currentHour + 1) % 24}:${currentMinute.toString().padStart(2, '0')}`,
      doctor: 'Dr. Chidi Okafor',
      specialty: 'Pediatrician',
      type: 'Child Health Review',
      mode: 'audio',
      status: 'confirmed',
      scheduledTime: new Date(new Date().setHours((currentHour + 1) % 24, currentMinute, 0, 0)),
    },
    {
      id: '3',
      date: now.toISOString(),
      time: `${(currentHour + 2) % 24}:${currentMinute.toString().padStart(2, '0')}`,
      doctor: 'Dr. Fatima Bello',
      specialty: 'General Practitioner',
      type: 'General Consultation',
      mode: 'chat',
      status: 'confirmed',
      scheduledTime: new Date(new Date().setHours((currentHour + 2) % 24, currentMinute, 0, 0)),
    },
    {
      id: '4',
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      time: '2:30 PM',
      doctor: 'Dr. Ibrahim Ahmed',
      specialty: 'Pediatrician',
      type: 'Child Immunization',
      mode: 'video',
      status: 'completed',
      scheduledTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    },
  ];

  const upcomingAppointment = mockAppointments.find(apt => apt.status === 'upcoming');

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                {t('welcomeBackExclaim')}
              </h1>
              <p className="text-gray-600 text-sm">{t('hereDashboardOverview')}</p>
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* Subscription Status */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('subscriptionStatus')}</p>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {hasActiveSubscription ? t('active') : t('inactive')}
                </h3>
                {hasActiveSubscription && subscription && (
                  <p className="text-xs text-gray-600">
                    {subscription.plan === 'monthly' ? t('monthlyPlan') : t('yearlyPlan')}
                  </p>
                )}
              </div>
              <div className={`p-2 rounded-lg ${hasActiveSubscription ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                {hasActiveSubscription ? <CheckCircle className="w-5 h-5 text-white" /> : <AlertCircle className="w-5 h-5 text-white" />}
              </div>
            </div>
            {hasActiveSubscription && subscription && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  {t('renews')}: {new Date(subscription.endDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {/* Next Appointment */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 mb-1">{t('nextAppointment')}</p>
                {upcomingAppointment ? (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {new Date(upcomingAppointment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </h3>
                    <p className="text-xs text-gray-600">{upcomingAppointment.time}</p>
                    <p className="text-xs text-gray-700 mt-2 truncate font-medium">{upcomingAppointment.type}</p>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">{t('noAppointments')}</p>
                )}
              </div>
              <div className="p-2 bg-emerald-500 rounded-lg flex-shrink-0 ml-3">
                <Calendar className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          {/* Health Records */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:col-span-2 lg:col-span-1">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('healthRecords')}</p>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">12</h3>
                <p className="text-xs text-gray-600">{t('totalRecords')}</p>
              </div>
              <div className="p-2 bg-pink-500 rounded-lg">
                <Activity className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                {t('lastUpdated')}: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Recent Appointments */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{t('recentAppointments')}</h2>
            <div className="space-y-3">
              {mockAppointments.map(appointment => (
                <div key={appointment.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className={`p-2 rounded-lg ${
                    appointment.status === 'completed' ? 'bg-emerald-500' : 'bg-emerald-500'
                  }`}>
                    {appointment.status === 'completed' ? <CheckCircle className="w-4 h-4 text-white" /> : <Clock className="w-4 h-4 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">{appointment.type}</h4>
                    <p className="text-xs text-gray-600 truncate mt-0.5">{appointment.doctor}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(appointment.date).toLocaleDateString()} at {appointment.time}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                    appointment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {appointment.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{t('quickActions')}</h2>
            <div className="space-y-2">
              <Link to="/patient/book-appointment" className="group w-full flex items-center justify-between p-3 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all border border-emerald-100">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-emerald-500 rounded-lg group-hover:scale-110 transition-transform">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{t('bookAppointment')}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-emerald-600 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link to="/patient/appointments" className="group w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-blue-500 rounded-lg group-hover:scale-110 transition-transform">
                    <Phone className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{t('viewAppointments')}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link to="/patient/child-health" className="group w-full flex items-center justify-between p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all border border-purple-100">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-purple-500 rounded-lg group-hover:scale-110 transition-transform">
                    <Baby className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{t('childHealth')}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-purple-600 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
};

export default PatientDashboard;
